/**
 * ForaVida — Homepage scripts.
 *
 * Dynamic showroom open/closed badge.
 * Opening hours (Amsterdam time, Europe/Amsterdam):
 *   Ma-Vr: 10:00–18:00
 *   Za-Zo: 09:00–17:00
 *
 * @package foravida
 */

(function () {
  'use strict';

  var faqAnswerId = 0;

  /**
   * Give an FAQ trigger an explicit relationship to its answer while
   * preserving any stable ID already supplied by authored content.
   */
  function linkFaqControlToAnswer(control, answer) {
    if (!control || !answer) return;

    if (!answer.id) {
      var candidateId;
      do {
        faqAnswerId += 1;
        candidateId = 'fv-faq-answer-' + faqAnswerId;
      } while (document.getElementById(candidateId));
      answer.id = candidateId;
    }

    control.setAttribute('aria-controls', answer.id);
  }

  /**
   * Returns { weekday: 'Mon'|'Tue'|…|'Sat'|'Sun', hour: 0-23, minute: 0-59 }
   * using the Europe/Amsterdam timezone so DST is handled correctly.
   */
  function getAmsterdamNow() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Amsterdam',
      weekday: 'short',  // 'Mon', 'Sat', 'Sun', …
      hour:    'numeric',
      minute:  'numeric',
      hour12:  false,
    }).formatToParts(new Date());

    var get = function (type) {
      return parts.filter(function (p) { return p.type === type; })[0].value;
    };

    return {
      weekday: get('weekday'),
      hour:    parseInt(get('hour'),   10),
      minute:  parseInt(get('minute'), 10),
    };
  }

  function isShowroomOpen() {
    var t       = getAmsterdamNow();
    var mins    = t.hour * 60 + t.minute;
    var weekend = t.weekday === 'Sat' || t.weekday === 'Sun';

    // Za-Zo 09:00–17:00 | Ma-Vr 10:00–18:00
    return weekend
      ? (mins >= 9 * 60 && mins < 17 * 60)
      : (mins >= 10 * 60 && mins < 18 * 60);
  }

  function updateShowroomBadge() {
    var badge = document.querySelector('.fv-showroom__open-badge');
    if (!badge) return;

    // Labels come from wp_localize_script (see foravida_enqueue_scripts
    // in inc/styles-and-scripts.php) so translations go through .mo files.
    // The Dutch literals below are last-resort fallbacks only (payload
    // missing) and must mirror the source strings in that PHP file.
    var i18n = (window.foravidaHomepageData && window.foravidaHomepageData.i18n) || {};
    var openLabel   = i18n.showroomOpen   || 'Nu geopend';
    var closedLabel = i18n.showroomClosed || 'Nu gesloten';

    if (isShowroomOpen()) {
      badge.textContent = openLabel;
      badge.classList.remove('fv-showroom__open-badge--closed');
    } else {
      badge.textContent = closedLabel;
      badge.classList.add('fv-showroom__open-badge--closed');
    }
  }

  /**
   * Hydrate an empty .fv-faq-list from the hidden Yoast FAQ block on the page.
   * Keeps Yoast as the single source of truth for questions, answers, and
   * FAQ schema — editors update one place, both the visible list and the
   * structured-data block stay in sync.
   */
  function hydrateFaqListFromYoast() {
    document.querySelectorAll('.fv-faq-list').forEach(function (list) {
      // Skip lists that already have authored content.
      // Skip if the list already holds authored accordion items OR the raw
      // Yoast schema block (rendered boxed) — otherwise the FAQ would double.
      if (list.querySelector('.fv-faq') || list.querySelector('.schema-faq')) return;

      var yoast = document.querySelector('.wp-block-yoast-faq-block');
      if (!yoast) return;

      var sections = yoast.querySelectorAll('.schema-faq-section');
      if (!sections.length) return;

      var frag = document.createDocumentFragment();
      var clonedItems = 0;
      sections.forEach(function (section) {
        var q = section.querySelector('.schema-faq-question');
        var a = section.querySelector('.schema-faq-answer');
        if (!q || !a) return;

        var item = document.createElement('details');
        item.className = 'fv-faq';
        item.open = 0 === clonedItems;

        var btn = document.createElement('summary');
        btn.className = 'fv-faq__question';
        var label = document.createElement('span');
        label.textContent = q.textContent.trim();
        btn.appendChild(label);
        var marker = document.createElement('span');
        marker.className = 'fv-faq__chevron';
        marker.setAttribute('aria-hidden', 'true');
        btn.appendChild(marker);

        var answer = document.createElement('div');
        answer.className = 'fv-faq__answer fv-faq__body';
        // Clone nodes (defence-in-depth vs. HTML re-serialisation) — same
        // pattern as foravida-faq-accordion.js.
        var p = document.createElement('p');
        Array.from(a.childNodes).forEach(function (n) {
          p.appendChild(n.cloneNode(true));
        });
        answer.appendChild(p);

        linkFaqControlToAnswer(btn, answer);
        item.appendChild(btn);
        item.appendChild(answer);
        frag.appendChild(item);
        clonedItems += 1;
      });

      // Only hide the source Yoast block after at least one complete FAQ item
      // has been cloned and inserted successfully. If source markup changes or
      // JavaScript fails earlier, the original FAQ remains visible.
      if (!clonedItems) return;
      list.appendChild(frag);
      list.classList.add('is-hydrated');
    });
  }

  /**
   * Legacy FAQ list toggle. Newly hydrated Yoast items use native
   * <details>/<summary>; this keeps already-authored div/button accordions
   * functional without changing their stored class vocabulary.
   *
   * Authored Gutenberg markup for this list sometimes ships with an inline
   * `onclick="this.parentElement.classList.toggle('fv-faq--open')"` on the
   * question element. If both the inline handler and this one fire, the
   * class toggles twice per click and the accordion appears frozen. Strip
   * the inline attribute before binding.
   */
  function initFaqList() {
    document.querySelectorAll('.fv-faq-list details.fv-faq').forEach(function (details) {
      var summary = details.querySelector('summary');
      var answer = details.querySelector('.fv-faq__answer, .fv-faq__body');
      linkFaqControlToAnswer(summary, answer);
    });

    document.querySelectorAll('.fv-faq-list button.fv-faq__question').forEach(function (btn) {
      var parent = btn.closest('.fv-faq');
      if (!parent) return;
      var answer = parent.querySelector('.fv-faq__answer, .fv-faq__body');
      linkFaqControlToAnswer(btn, answer);
      if (btn.hasAttribute('onclick')) btn.removeAttribute('onclick');
      btn.setAttribute('aria-expanded', parent.classList.contains('fv-faq--open') ? 'true' : 'false');

      btn.addEventListener('click', function () {
        var isOpen = parent.classList.toggle('fv-faq--open');
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
  }

  function init() {
    updateShowroomBadge();
    hydrateFaqListFromYoast();
    initFaqList();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
